import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

import { DEFAULT_TAB_WIDTH, QUANTUM, TAB_NODE_NAME } from './constants';

type TGetMinDirtyTabPosArgs = {
  diffStart: number;
  newParagraph: PMNode;
  oldNode: PMNode | null;
  paragraphPos: number;
};

export const getMinDirtyTabPos = ({
  diffStart,
  newParagraph,
  oldNode,
  paragraphPos,
}: TGetMinDirtyTabPosArgs): number | null => {
  const paragraphContentStart = paragraphPos + 1;

  let minOffset = Math.max(0, diffStart - paragraphContentStart);
  if (oldNode?.type.name === newParagraph.type.name) {
    // Attr-only paragraph changes still need a full tab relayout when the paragraph style
    // changes text metrics before the tab (for example a footnote-sized paragraph).
    minOffset = oldNode.content.findDiffStart(newParagraph.content) ?? 0;
  }

  let firstTabPos: number | null = null;
  newParagraph.forEach((child, offset) => {
    if (firstTabPos != null) return;
    if (offset < minOffset) return;
    if (child.type.name !== TAB_NODE_NAME) return;
    firstTabPos = paragraphContentStart + offset;
  });

  return firstTabPos;
};

export const quantize = (px: number): number => Math.round(px / QUANTUM) * QUANTUM;

const measureCssWidth = (host: Element, name: string, raw: string): number => {
  const probe = document.createElement('div');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.padding = '0';
  probe.style.margin = '0';
  probe.style.border = '0';
  probe.style.height = '0';
  probe.style.display = 'block';
  probe.style.width = raw || `var(${name})`;
  host.appendChild(probe);
  const measured = probe.getBoundingClientRect().width;
  probe.remove();
  return measured;
};

export const pxFromCssVar = (el: Element, name: string): number => {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  if (Number.isFinite(n)) return n;

  // calc()/var() chains can't be parsed numerically; measure in DOM instead.
  const measured = measureCssWidth(el, name, raw);
  return measured || DEFAULT_TAB_WIDTH;
};

// Tab size shrinks inside tables to keep list/tabs aligned with compact table spacing.
export const tabVarForBlock = (block: HTMLElement): string =>
  block.closest('table') ? '--tab-width-table' : '--tab-width';

// posAtDOM can land on either side of an atom span; probe ±1 to find the tab node.
export const posOfTab = (view: EditorView, span: HTMLElement): number | null => {
  let pos: number;
  try {
    pos = view.posAtDOM(span, 0);
  } catch {
    return null;
  }

  const size = view.state.doc.content.size;
  if (!Number.isFinite(pos) || pos < 0 || pos > size) return null;

  const at = (p: number) => p >= 0 && p <= size && view.state.doc.nodeAt(p)?.type?.name === TAB_NODE_NAME;
  if (at(pos)) return pos;
  if (at(pos - 1)) return pos - 1;
  if (at(pos + 1)) return pos + 1;
  return null;
};

export const putWidthIntoStyle = (style: string | null | undefined, px: number): string => {
  const s = (style || '').trim();
  if (!s) return `width: ${px}px`;
  const parts = s
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean);
  let found = false;
  for (let i = 0; i < parts.length; i++) {
    const [k] = parts[i].split(':').map((t) => t.trim().toLowerCase());
    if (k === 'width') {
      parts[i] = `width: ${px}px`;
      found = true;
      break;
    }
  }
  if (!found) parts.push(`width: ${px}px`);
  return parts.join('; ');
};

export const inlineStartLeft = (block: HTMLElement): number => {
  let host: HTMLElement = block;
  if (block.tagName === 'LI') {
    const p = block.querySelector('p');
    if (p) host = p as HTMLElement;
  }
  const probe = document.createElement('span');
  probe.style.cssText = 'display:inline-block;width:0;height:0;margin:0;padding:0;border:0;';
  host.insertBefore(probe, host.firstChild);
  const left = probe.getBoundingClientRect().left;
  probe.remove();
  return left;
};
